/**
 * The payment catalog's plain data — the single source of truth imported by
 * BOTH the runtime catalog (`catalog.ts`, which layers Effect schemas on top)
 * and the Stripe catalog declaration `packages/infra/src/stripe.ts` reads.
 *
 * Plain constants and pure functions only — NO runtime imports, NO side
 * effects — so the browser bundle carries the plan table without the Effect
 * schemas layered on it. Type-only imports are erased and therefore fine.
 */
import type { BillingInterval, PlanKey } from '@listening/api-contract/schemas';

/**
 * What the server knows about a plan a client may ask for.
 *
 * `displayAmountCents` is a default for SSR and offline rendering only —
 * `GET /billing/checkout-config` returns the authoritative resolved amount,
 * which differs per price-experiment arm.
 *
 * **No price IDs.** The mapping from plan to Stripe price is server-only and
 * goes through `lookup_key` (`@listening/backend/stripe/price-resolver`), so
 * changing a price is a Stripe change rather than a deploy.
 */
export type PlanDefinition = {
  readonly interval: BillingInterval;
  readonly displayAmountCents: number;
  /**
   * `false` means "reachable only through a promo code or a specific offer
   * flow" — enforced server-side in `POST /billing/subscribe`, never by the
   * client omitting it from a list.
   */
  readonly selectable: boolean;
  /**
   * Trial length when no experiment is running. The server re-reads this at
   * creation, so a stale config response can never buy a trial.
   */
  readonly defaultTrialDays: number;
};

export const plans = {
  monthly_default: {
    interval: 'month',
    displayAmountCents: 1299,
    selectable: true,
    defaultTrialDays: 3,
  },
  yearly_default: {
    interval: 'year',
    displayAmountCents: 3900,
    selectable: true,
    defaultTrialDays: 3,
  },
  yearly_promo_59: {
    interval: 'year',
    displayAmountCents: 5900,
    selectable: false,
    defaultTrialDays: 3,
  },
  yearly_promo_69: {
    interval: 'year',
    displayAmountCents: 6900,
    selectable: false,
    defaultTrialDays: 3,
  },
  /**
   * **Deliberately unsupported through `POST /billing/subscribe`.** It is
   * `selectable: false` and, unlike the two promo plans, no promo code unlocks
   * it: the only thing that could is offer context, and the second-offer
   * paywall surface it serves is slated for removal, so none is built. Asking
   * for it is a 400. If that removal lands, the key leaves `PlanKey` too.
   */
  yearly_second_offer: {
    interval: 'year',
    displayAmountCents: 2999,
    selectable: false,
    defaultTrialDays: 3,
  },
} as const satisfies Record<PlanKey, PlanDefinition>;

/**
 * The plan vocabulary, as the keys of `plans` rather than a second list.
 *
 * `PLAN_KEYS` in `@listening/api-contract/schemas` is the same set derived from
 * the `PlanKey` schema's own literals; this one exists because that module
 * reaches `effect`, which the leaf constraint above forbids. The two cannot
 * drift: `plans satisfies Record<PlanKey, PlanDefinition>` rejects both a wrong
 * key and a missing one, so these keys are exactly `PlanKey`'s members.
 */
export const PLAN_KEYS = Object.keys(plans) as ReadonlyArray<
  keyof typeof plans
>;

/**
 * One arm of a price experiment.
 *
 * Variants live **under** a `PlanKey` rather than beside it, so the client keeps
 * posting `plan_id` and never learns an experiment is running. Making each
 * variant its own `PlanKey` would hand the client the choice of price, which is
 * exactly what the closed write catalog exists to prevent.
 *
 * `lookupKey` — never a raw price ID — is Stripe's own answer to "changing a
 * price should not require a deploy". Resolution happens server-side.
 *
 * Grandfathering needs no machinery: Stripe renews against the price object
 * attached to the subscription, so a subscriber keeps the arm they signed up on
 * forever. What Stripe does *not* record is *why*, which is the only reason the
 * server writes the assignment to `subscription_history`.
 *
 * `catalog.ts` carries the Effect schema for this shape; the plain type lives
 * here so the declaration can name it without reaching `effect`.
 *
 * @see docs/projects/active/refactor-payments/price-experiments.md
 */
export type PriceVariant = {
  /** Stripe `lookup_key`. Resolved to a price ID server-side, never client-side. */
  readonly lookupKey: string;
  /** Experiment arm name, e.g. `control` / `test_49`. Never sent to the client. */
  readonly variant: string;
  readonly isControl: boolean;
  /** What the arm charges. The control's is the plan's `displayAmountCents`. */
  readonly unitAmountCents: number;
};

/**
 * The metadata key a variant price carries to name the `PlanKey` it is a
 * variant **of**.
 *
 * The read side needs to answer "which plan is this subscriber on?" for a price
 * whose `lookup_key` is `yearly_default_v49` — not a `PlanKey`, so
 * `planDescriptorFromPrice` would derive `plan_id: null` and silently set
 * `can_change_plan_in_app: false` for the whole test cohort, forever (the price
 * grandfathers). Widening `PlanKey` would hand the client the choice of price;
 * a server-side lookup table would be a second source of truth for something
 * Stripe can hold. So the pointer lives **on the price object**, written by the
 * provisioning script and verified by the catalog diff.
 *
 * The **write** vocabulary does not change: requests still carry only a
 * `PlanKey`, and the server still picks the variant price via the experiment.
 *
 * @see docs/projects/active/refactor-payments/price-experiments.md
 */
export const PLAN_KEY_METADATA_KEY = 'listening_plan_key';

/**
 * The Stripe entitlement feature that means "has the product".
 *
 * Read on **both** sides: the provisioning script attaches it to every product,
 * and `entitlement.ts`'s access rule asks whether the customer holds it. It
 * lives in this leaf rather than beside that rule because the declaration needs
 * it and `entitlement.ts` reaches `effect`. One string, one definition.
 */
export const FULL_ACCESS_FEATURE = 'full_access';

const controlVariantFor = (planKey: PlanKey): PriceVariant => ({
  lookupKey: planKey,
  variant: 'control',
  isControl: true,
  unitAmountCents: plans[planKey].displayAmountCents,
});

/**
 * A pricing-experiment arm, keyed by the arm key its subscriptions are stamped
 * with, and the annual price it quotes.
 *
 * The lookup keys follow the documented `<planKey>_v<amount>` convention. They
 * name prices that **already exist and already carry live subscribers** — the
 * test has been running on them through the legacy endpoints — so provisioning
 * assigns the key to that price rather than minting a second one at the same
 * amount. Two price objects per arm would split each arm's cohort at the
 * cutover and break the grandfathered-price exemption, which keys on the prices
 * an account was actually billed at.
 *
 * That holds for the $79 arm, adopted onto the live $79 price. It cannot hold
 * for the $59 arm: the one live $59 price was also the `yearly_promo_59` promo,
 * a price carries one lookup key, and the promo has the older claim — so in
 * production `yearly_default_v59` is a new price, and arm subscribers already
 * billed on the old one keep billing there and read as `yearly_promo_59`. Their
 * plan changes still follow the `pricing_variant` stamp on the subscription,
 * and the grandfathered exemption still sees the price they were billed at.
 * See `packages/infra/src/stripe-production-adoption.ts`.
 */
const annualArm = (
  variant: string,
  lookupKey: string,
  unitAmountCents: number,
): PriceVariant => ({ lookupKey, variant, isControl: false, unitAmountCents });

/**
 * Every plan's variant set, keyed by the **arm name** an assignment carries.
 *
 * A non-control entry is a real Stripe price: the catalog declaration emits it,
 * the provisioning script creates it stamped with its base `PlanKey`, and the
 * diff reports a missing stamp rather than repairing one.
 *
 * ⚠️ **Arms and prices are not 1:1, and the entries below are arms.**
 * `pricing-test-v1` was a 3×2 factorial — three annual prices crossed with a
 * trial dimension — and `pricing-test-v2` reuses two of those prices, so eight
 * arms resolve to three prices, and the $39 arms resolve to the *canonical*
 * key because their price is control's. The trial
 * half is not a price property at all: it is a create-call parameter, resolved
 * through `evaluateTrialDays`. `experimentVariants` therefore dedupes to the
 * distinct non-canonical lookup keys, which is what "the extra prices to
 * provision" means.
 *
 * @see docs/projects/active/pricing-experiment-ab-test-6-leg.md
 */
export const planVariants = {
  // Every arm quotes $12.99 monthly, so the monthly plan has no price arms.
  // An arm still keeps its trial dimension on a monthly selection, and an arm
  // name this plan does not list resolves to control by `resolveVariant`.
  monthly_default: [controlVariantFor('monthly_default')],
  yearly_default: [
    controlVariantFor('yearly_default'),
    annualArm('price-39-trial', 'yearly_default', 3900),
    annualArm('price-59-no-trial', 'yearly_default_v59', 5900),
    annualArm('price-59-trial', 'yearly_default_v59', 5900),
    annualArm('price-79-no-trial', 'yearly_default_v79', 7900),
    annualArm('price-79-trial', 'yearly_default_v79', 7900),
    annualArm('pricing-test-v2:control', 'yearly_default', 3900),
    annualArm('pricing-test-v2:price-59-trial', 'yearly_default_v59', 5900),
  ],
  yearly_promo_59: [controlVariantFor('yearly_promo_59')],
  yearly_promo_69: [controlVariantFor('yearly_promo_69')],
  yearly_second_offer: [controlVariantFor('yearly_second_offer')],
} as const satisfies Record<PlanKey, ReadonlyArray<PriceVariant>>;

/**
 * The extra prices to provision: the distinct lookup keys that are not the
 * plan's canonical one.
 *
 * Deduped because several arms share a price — see the warning on
 * `planVariants`. Emitting one `DeclaredPrice` per arm would declare the same
 * lookup key twice and re-declare the canonical price as a variant of itself.
 */
export const experimentVariants = (
  planKey: PlanKey,
): ReadonlyArray<PriceVariant> => {
  const seen = new Set<string>([planKey]);
  return planVariants[planKey].filter((variant) => {
    if (variant.isControl || seen.has(variant.lookupKey)) return false;
    seen.add(variant.lookupKey);
    return true;
  });
};
